import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { extractUrls, fetchMultipleUrls } from '../utils/openGraphService';
import logger from '../utils/logger';

export const useOpenGraph = (text, options = {}) => {
  const [openGraphData, setOpenGraphData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const {
    enabled = true,
    onDataLoaded = null,
    debounceMs = 600, // delay before surfacing cards while typing
  } = options;

  // Track latest request to ignore stale responses
  const requestIdRef = useRef(0);

  // Extract URLs from text
  const urls = useMemo(() => extractUrls(text || ''), [text]);

  // Fetch OpenGraph data for URLs
  const fetchData = useCallback(async () => {
    if (!enabled || urls.length === 0) {
      setOpenGraphData([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await fetchMultipleUrls(urls);
      setOpenGraphData(data);

      if (onDataLoaded) {
        onDataLoaded(data);
      }
    } catch (err) {
      logger.error('useOpenGraph: Error occurred', err);
      setError(err);
      // Set empty data on error to prevent infinite loading
      setOpenGraphData([]);
    } finally {
      setIsLoading(false);
    }
  }, [urls, enabled, onDataLoaded]);

  // Fetch data when text changes, debounced
  useEffect(() => {
    let isMounted = true;

    // If disabled or no URLs, clear and skip
    if (!enabled || urls.length === 0) {
      setOpenGraphData([]);
      setIsLoading(false);
      setError(null);
      return () => {
        isMounted = false;
      };
    }

    const currentId = ++requestIdRef.current;
    const timer = setTimeout(
      async () => {
        if (!isMounted) return;
        // Only proceed if this is the latest scheduled request
        if (currentId !== requestIdRef.current) return;
        await fetchData();
      },
      Math.max(0, debounceMs)
    );

    // Cleanup on change/unmount
    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [fetchData, enabled, urls, debounceMs]);

  // Refresh data manually
  const refresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  // Clear data
  const clear = useCallback(() => {
    setOpenGraphData([]);
    setError(null);
  }, []);

  const result = {
    urls,
    openGraphData,
    isLoading,
    error,
    refresh,
    clear,
    hasUrls: urls.length > 0,
  };

  return result;
};
